import { useEffect, useId, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import MayaOutlinerIcon from './MayaOutlinerIcon'
import MayaOutlinerRowShell from './MayaOutlinerRowShell'
import MayaOutlinerScrollArea from './MayaOutlinerScrollArea'
import NavIcon from './NavIcon'
import { SidebarPanelContent } from './SidebarPanel'
import { featuredProjects, navLinks } from '../data/portfolio'
import { RIG_OVERLAY_EVENT, JOINT_OVERLAY_EVENT, emitRigOverlay, emitJointOverlay, readJointOverlayState, readRigOverlayState, readShowJointOverlay, readShowRigOverlay } from '../lib/hubTools'
import { ASSET_IDS } from '../lib/sceneLoadTracker'
import { useAssetReady } from '../lib/hooks/useSceneReadiness'
import { getProjectDetailsPath, getProjectPath } from '../lib/utils/projects'
import { requestNavigation } from '../lib/transitionCoordinator'

const CHARACTER_ASSET_IDS = {
  sakira: { mesh: ASSET_IDS.sakiraMesh, rig: ASSET_IDS.sakiraRig },
  song: { mesh: ASSET_IDS.songMesh, rig: ASSET_IDS.songRig },
}
const PORTFOLIO_LABEL = 'portfolio'
const CHARACTERS_LABEL = 'characters'
const CHARACTER_NODES = [
  { id: 'song', label: 'song' },
  { id: 'sakira', label: 'sakira' },
]

const MAIN_CAMERA_LABEL = 'Main Camera'
const DEFAULT_CAMERAS = [MAIN_CAMERA_LABEL, 'persp', 'top', 'front', 'side']
const DEFAULT_OUTLINER_SETS = ['defaultLightSet', 'defaultObjectSet']

const showreelNavLink = navLinks.find((link) => link.to === '/')
const contactNavLink = navLinks.find((link) => link.to === '/contact')
const aboutNavLink = navLinks.find((link) => link.to === '/about')

const getProjectIdFromPath = (pathname) => {
  const match = pathname.match(/^\/projects\/([^/]+)(?:\/details)?\/?$/)
  return match?.[1] || null
}

const OutlinerPanelExpandBox = ({
  isExpanded,
  onClick,
  ariaControls,
  ariaLabel,
  inert = false,
}) => {
  if (!onClick) {
    return (
      <span
        className={['maya-outliner-expand', isExpanded ? 'is-expanded' : 'is-collapsed'].join(' ')}
        aria-expanded={isExpanded}
        tabIndex={inert ? -1 : undefined}
        aria-hidden={inert ? true : undefined}
      />
    )
  }

  return (
    <button
      type="button"
      className={['maya-outliner-expand', isExpanded ? 'is-expanded' : 'is-collapsed'].join(' ')}
      onClick={onClick}
      aria-expanded={isExpanded}
      aria-controls={ariaControls}
      aria-label={ariaLabel}
    />
  )
}

const OutlinerPanelIconGroup = ({
  isExpandable,
  isExpanded,
  navIconType,
  mayaIconType,
  project,
  onExpandClick,
  expandAriaControls,
  expandAriaLabel,
  expandInert = false,
}) => (
  <span className="maya-outliner-panel-icon-group">
    {isExpandable ? (
      <OutlinerPanelExpandBox
        isExpanded={isExpanded}
        onClick={onExpandClick}
        ariaControls={expandAriaControls}
        ariaLabel={expandAriaLabel}
        inert={expandInert}
      />
    ) : null}
    <OutlinerPanelIcon navIconType={navIconType} mayaIconType={mayaIconType} project={project} />
  </span>
)

const OUTLINER_PANEL_ICON_CLASS =
  'maya-outliner-panel-icon site-frame flex shrink-0 items-center justify-center bg-secondary'

const OutlinerPanelIcon = ({ navIconType, mayaIconType, project }) => {
  if (navIconType) return <NavIcon type={navIconType} compact className="maya-outliner-panel-icon" />

  if (project) {
    return (
      <span className={`${OUTLINER_PANEL_ICON_CLASS} text-[9px] uppercase`}>
        {project.iconImage ? (
          <img
            src={`${process.env.PUBLIC_URL}${project.iconImage}`}
            alt=""
            className="h-full w-full object-cover"
            aria-hidden="true"
          />
        ) : (
          project.title.charAt(0)
        )}
      </span>
    )
  }

  return (
    <span className={OUTLINER_PANEL_ICON_CLASS}>
      <MayaOutlinerIcon type={mayaIconType || 'transform'} />
    </span>
  )
}

const getOutlinerPanelClassName = () =>
  [
    'site-panel flex items-center gap-4 p-3 transition-colors',
    'maya-outliner-panel-row site-panel-no-intro text-left',
  ]
    .filter(Boolean)
    .join(' ')

const OutlinerRigToggle = ({ checked, onChange, ariaLabel, loading = false }) => {
  const handleClick = (event) => {
    event.stopPropagation()
    if (loading) return
    onChange(!checked)
  }

  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      title={ariaLabel}
      onClick={handleClick}
      data-loading={loading ? 'true' : undefined}
      aria-busy={loading ? 'true' : undefined}
      className={[
        'site-toggle maya-outliner-rig-switch ml-auto inline-flex h-5 w-9 shrink-0 items-center rounded-full border p-[2px] transition-colors duration-200',
        checked ? 'site-toggle-on' : 'site-toggle-off',
      ].join(' ')}
    >
      <span
        aria-hidden="true"
        className={[
          'site-toggle-thumb maya-outliner-rig-switch-thumb inline-flex h-3 min-w-[12px] shrink-0 items-center justify-center rounded-full px-[2px] text-[8px] font-semibold leading-none transition-transform duration-200 ease-in-out',
          checked ? 'translate-x-[1.125rem]' : 'translate-x-0',
        ].join(' ')}
      >
        H
      </span>
    </button>
  )
}

const MayaOutlinerRigRow = ({
  depth = 0,
  label,
  characterLabel,
  overlayKind = 'control rig',
  showOverlay,
  onToggleOverlay,
  loading = false,
}) => {
  const panelIcon = (
    <OutlinerPanelIconGroup mayaIconType="nurbsCurve" />
  )
  const rigToggleLabel = showOverlay
    ? `Hide ${characterLabel} ${overlayKind}`
    : `Show ${characterLabel} ${overlayKind}`

  return (
    <MayaOutlinerRowShell layout="panel" depth={depth} className="has-rig-row">
      <div
        className={[
          getOutlinerPanelClassName(),
          'maya-outliner-rig-panel',
          showOverlay ? '' : 'is-rig-hidden',
        ]
          .filter(Boolean)
          .join(' ')}
        aria-hidden={showOverlay ? undefined : 'true'}
        aria-label={
          showOverlay
            ? `${characterLabel} ${overlayKind}`
            : `${characterLabel} ${overlayKind} — hidden`
        }
      >
        <SidebarPanelContent
          icon={panelIcon}
          title={label}
          trailing={
            <OutlinerRigToggle
              checked={showOverlay}
              onChange={onToggleOverlay}
              ariaLabel={rigToggleLabel}
              loading={loading}
            />
          }
        />
      </div>
    </MayaOutlinerRowShell>
  )
}

const MayaOutlinerCharacterRigRows = ({
  character,
  charactersTreeId,
  showControlRig,
  showJointRow,
  rigOverlayByCharacter,
  jointOverlayByCharacter,
  onToggleRig,
  onToggleJoints,
}) => {
  const assets = CHARACTER_ASSET_IDS[character.id]
  const meshReady = useAssetReady(assets ? [assets.mesh] : [])
  const rigReady = useAssetReady(assets ? [assets.rig] : [])
  const meshLoading = !meshReady.ready || meshReady.loading
  const rigJsonLoading =
    (rigOverlayByCharacter[character.id] || jointOverlayByCharacter[character.id])
    && (!rigReady.ready || rigReady.loading)
  const controlLoading = meshLoading || (rigOverlayByCharacter[character.id] && rigJsonLoading)
  const jointsLoading = meshLoading || (jointOverlayByCharacter[character.id] && rigJsonLoading)

  return (
    <div
      id={`${charactersTreeId}-${character.id}`}
    >
      {showControlRig ? (
        <MayaOutlinerRigRow
          depth={3}
          label="controlRig"
          characterLabel={character.label}
          overlayKind="control rig"
          showOverlay={rigOverlayByCharacter[character.id]}
          onToggleOverlay={() => {
            if (meshLoading) return
            onToggleRig(character.id)
          }}
          loading={controlLoading}
        />
      ) : null}
      {showJointRow ? (
        <MayaOutlinerRigRow
          depth={3}
          label="joints"
          characterLabel={character.label}
          overlayKind="joints"
          showOverlay={jointOverlayByCharacter[character.id]}
          onToggleOverlay={() => {
            if (meshLoading) return
            onToggleJoints(character.id)
          }}
          loading={jointsLoading}
        />
      ) : null}
    </div>
  )
}

const MayaOutlinerRow = ({
  depth = 0,
  isExpanded = false,
  isExpandable = false,
  navIconType,
  mayaIconType,
  project,
  label,
  isActive = false,
  selectionTier = 'none',
  isStatic = false,
  isDimmed = false,
  smallIcon = false,
  to,
  onClick,
  onExpandClick,
  expandLocked = false,
  ariaLabel,
  ariaControls,
}) => {
  if (isStatic) {
    const className = ['maya-outliner-row', 'is-static', isDimmed ? 'is-dimmed' : '']
      .filter(Boolean)
      .join(' ')

    return (
      <MayaOutlinerRowShell depth={depth}>
        <div className={className} aria-hidden={isDimmed ? undefined : 'true'} aria-label={ariaLabel}>
          <span className="maya-outliner-icon-slot">
            <MayaOutlinerIcon
              type={mayaIconType || 'transform'}
              size={smallIcon ? 'small' : 'default'}
            />
          </span>
          <span className="maya-outliner-label">{label}</span>
        </div>
      </MayaOutlinerRowShell>
    )
  }

  const expanded = expandLocked || isExpanded
  const expandBoxClick = expandLocked ? undefined : onExpandClick
  const groupToggleClick =
    expandLocked && isExpandable && !onExpandClick ? undefined : onClick

  const panelIcon = (
    <OutlinerPanelIconGroup
      isExpandable={isExpandable && !onExpandClick}
      isExpanded={expanded}
      navIconType={navIconType}
      mayaIconType={mayaIconType}
      project={project}
      onExpandClick={expandBoxClick}
      expandAriaControls={ariaControls}
      expandAriaLabel={
        expanded
          ? `Collapse ${ariaLabel || label}`
          : `Expand ${ariaLabel || label}`
      }
      expandInert={expandLocked}
    />
  )

  const expandControl =
    isExpandable && onExpandClick ? (
      <span className="maya-outliner-expand-slot">
        <OutlinerPanelExpandBox
          isExpanded={expanded}
          onClick={expandBoxClick}
          ariaControls={ariaControls}
          ariaLabel={
            expanded
              ? `Collapse ${ariaLabel || label}`
              : `Expand ${ariaLabel || label}`
          }
          inert={expandLocked}
        />
      </span>
    ) : null

  const panelContent = <SidebarPanelContent icon={panelIcon} title={label} />

  const resolvedSelectionTier =
    selectionTier !== 'none' ? selectionTier : isActive ? 'primary' : 'none'

  const panelClassName = getOutlinerPanelClassName()

  const handleNavigate = (event) => {
    if (!to) return
    event.preventDefault()
    requestNavigation(to, { explicit: true })
  }

  const panelElement = to ? (
    <button
      type="button"
      className={panelClassName}
      onClick={handleNavigate}
      aria-label={ariaLabel || label}
      aria-current={resolvedSelectionTier === 'primary' ? 'page' : undefined}
    >
      {panelContent}
    </button>
  ) : (
    <button
      type="button"
      className={panelClassName}
      onClick={groupToggleClick}
      aria-label={ariaLabel || label}
      aria-expanded={isExpandable && !onExpandClick ? expanded : undefined}
      aria-controls={isExpandable && !onExpandClick ? ariaControls : undefined}
    >
      {panelContent}
    </button>
  )

  return (
    <MayaOutlinerRowShell layout="panel" depth={depth} selectionTier={resolvedSelectionTier}>
      {expandControl}
      {panelElement}
    </MayaOutlinerRowShell>
  )
}

const OutlinerDrawerBody = ({ wrap, open, id, children }) => {
  if (!wrap) return children
  return (
    <div id={id} className={open ? 'flat-drawer is-open' : 'flat-drawer'}>
      <div className="flat-drawer-inner">{children}</div>
    </div>
  )
}

const SidebarOutliner = ({
  introStaggerMs = 188,
  showCharacters = true,
  drawerOpen = false,
  onToggleDrawer,
  drawerId = 'flat-drawer',
}) => {
  const location = useLocation()
  const portfolioTreeId = useId()
  const projectsTreeId = useId()
  const charactersTreeId = useId()
  const projectId = getProjectIdFromPath(location.pathname)
  const isProjectsRoute =
    location.pathname.startsWith('/projects/')
  const isShowreelRoute = location.pathname === '/'
  const isContactRoute = location.pathname === '/contact'
  const isAboutRoute = location.pathname === '/about'
  const [isPortfolioOpen, setIsPortfolioOpen] = useState(true)
  const [isProjectsOpen, setIsProjectsOpen] = useState(false)
  const [isCharactersOpen, setIsCharactersOpen] = useState(false)
  const [openCharacterIds, setOpenCharacterIds] = useState(() =>
    Object.fromEntries(CHARACTER_NODES.map(({ id }) => [id, false]))
  )
  const [openProjectIds, setOpenProjectIds] = useState(() =>
    Object.fromEntries(featuredProjects.map(({ id }) => [id, false]))
  )
  const [rigOverlayByCharacter, setRigOverlayByCharacter] = useState(() => readRigOverlayState())
  const [jointOverlayByCharacter, setJointOverlayByCharacter] = useState(() => readJointOverlayState())
  const [searchQuery, setSearchQuery] = useState('')
  const lockExpanded = Boolean(onToggleDrawer)

  useEffect(() => {
    if (isProjectsRoute) setIsProjectsOpen(true)
  }, [isProjectsRoute])

  useEffect(() => {
    if (!projectId) return
    setOpenProjectIds((current) => ({
      ...current,
      [projectId]: true,
    }))
  }, [projectId])

  useEffect(() => {
    const handleRigOverlay = (event) => {
      if (event.detail?.characters) {
        setRigOverlayByCharacter({ ...event.detail.characters })
      }
    }
    const handleJointOverlay = (event) => {
      if (event.detail?.characters) {
        setJointOverlayByCharacter({ ...event.detail.characters })
      }
    }
    window.addEventListener(RIG_OVERLAY_EVENT, handleRigOverlay)
    window.addEventListener(JOINT_OVERLAY_EVENT, handleJointOverlay)
    return () => {
      window.removeEventListener(RIG_OVERLAY_EVENT, handleRigOverlay)
      window.removeEventListener(JOINT_OVERLAY_EVENT, handleJointOverlay)
    }
  }, [])

  const normalizedQuery = searchQuery.trim().toLowerCase()

  const matchesSearch = (label) =>
    !normalizedQuery || label.toLowerCase().includes(normalizedQuery)

  const filteredProjects = useMemo(() => {
    if (!normalizedQuery) return featuredProjects
    return featuredProjects.filter((project) => {
      const haystack = `${project.title} ${project.sidebarSubtitle || project.categoryLabel} explore`
      return haystack.toLowerCase().includes(normalizedQuery)
    })
  }, [normalizedQuery])

  const showDefaultCameras =
    !normalizedQuery ||
    DEFAULT_CAMERAS.some((camera) => camera.toLowerCase().includes(normalizedQuery))

  const showDefaultSets =
    !normalizedQuery ||
    DEFAULT_OUTLINER_SETS.some((setName) => setName.toLowerCase().includes(normalizedQuery))

  const showShowreel = matchesSearch('showreel')
  const showContact = matchesSearch('contact')
  const showAbout = matchesSearch('about')

  const showProjectsGroup =
    matchesSearch('projects') || filteredProjects.length > 0

  const filteredCharacters = useMemo(() => {
    if (!normalizedQuery) return CHARACTER_NODES
    return CHARACTER_NODES.filter(({ label }) => {
      const haystack = `${label} controlrig control rig joints skeleton characters`
      return haystack.toLowerCase().includes(normalizedQuery)
    })
  }, [normalizedQuery])

  const showCharactersGroup =
    showCharacters &&
    (
      matchesSearch(CHARACTERS_LABEL) ||
      matchesSearch('controlrig') ||
      matchesSearch('control rig') ||
      matchesSearch('joints') ||
      matchesSearch('skeleton') ||
      filteredCharacters.length > 0
    )

  const showPortfolioChildren =
    showShowreel || showContact || showAbout || showProjectsGroup || showCharactersGroup

  const showPortfolioGroup =
    matchesSearch(PORTFOLIO_LABEL) || showPortfolioChildren

  const handleToggleProjects = () => {
    const nextOpen = !isProjectsOpen
    setIsProjectsOpen(nextOpen)
    if (nextOpen) {
      setIsPortfolioOpen(true)
    }
  }

  const showProjectsRow = showProjectsGroup
  const showCharactersRow = showCharactersGroup

  const isPortfolioInSelectionPath =
    isShowreelRoute || isContactRoute || isAboutRoute || isProjectsRoute

  const getProjectSelectionTier = (id) => {
    if (location.pathname === getProjectDetailsPath(id)) return 'ancestor'
    if (projectId === id) return 'primary'
    return 'none'
  }

  const getExploreSelectionTier = (id) => {
    if (location.pathname === getProjectDetailsPath(id)) return 'primary'
    return 'none'
  }

  const getProjectsGroupSelectionTier = () => {
    if (isProjectsRoute && !projectId) return 'primary'
    if (isProjectsRoute && projectId) return 'ancestor'
    return 'none'
  }

  const handleToggleCharacter = (characterId) => {
    setOpenCharacterIds((current) => ({
      ...current,
      [characterId]: !current[characterId],
    }))
    setIsCharactersOpen(true)
    setIsPortfolioOpen(true)
  }

  const handleToggleProjectExpand = (id) => {
    setOpenProjectIds((current) => ({
      ...current,
      [id]: !current[id],
    }))
  }

  const handleNavigateToProject = (id) => {
    requestNavigation(getProjectPath(id), { explicit: true })
  }

  const handleToggleRigControls = (characterId) => {
    emitRigOverlay({ characterId, show: !readShowRigOverlay(characterId) })
  }

  const handleToggleJoints = (characterId) => {
    emitJointOverlay({ characterId, show: !readShowJointOverlay(characterId) })
  }

  return (
    <nav
      className="maya-outliner site-intro-item"
      style={{ '--intro-delay': `${2 * introStaggerMs}ms` }}
      aria-label="Outliner"
    >
      {onToggleDrawer ? null : (
        <div className="maya-outliner-titlebar">
          <span className="maya-outliner-tab">Outliner</span>
        </div>
      )}

      {onToggleDrawer ? null : (
        <div className="maya-outliner-menubar">
          <span className="maya-outliner-menu-item">Display</span>
          <span className="maya-outliner-menu-item">Show</span>
          <span className="maya-outliner-menu-item">Help</span>
        </div>
      )}

      <OutlinerDrawerBody
        wrap={Boolean(onToggleDrawer)}
        open={drawerOpen}
        id={drawerId}
      >
      <div className="maya-outliner-search-wrap">
        <label className="maya-outliner-search">
          <span className="sr-only">Search outliner</span>
          <input
            type="search"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder="Search..."
            className="maya-outliner-search-input"
          />
          <span className="maya-outliner-search-dropdown" aria-hidden="true" />
        </label>
      </div>

      <div className="maya-outliner-inset-frame">
        <MayaOutlinerScrollArea>
          <div className="maya-outliner-tree-inner">
            {showDefaultCameras
              ? DEFAULT_CAMERAS.filter((camera) => matchesSearch(camera)).map((camera) => (
                  <MayaOutlinerRow
                    key={camera}
                    depth={0}
                    label={camera}
                    isStatic
                    smallIcon
                    isDimmed={camera !== MAIN_CAMERA_LABEL}
                    mayaIconType="camera"
                  />
                ))
              : null}

            {showPortfolioGroup ? (
              <>
                <MayaOutlinerRow
                  depth={0}
                  label={PORTFOLIO_LABEL}
                  mayaIconType="transform"
                  selectionTier={isPortfolioInSelectionPath ? 'ancestor' : 'none'}
                  isExpandable
                  isExpanded={lockExpanded || isPortfolioOpen}
                  expandLocked={lockExpanded}
                  onClick={lockExpanded ? undefined : () => setIsPortfolioOpen((open) => !open)}
                  ariaControls={portfolioTreeId}
                />

                {(lockExpanded || isPortfolioOpen) && showPortfolioChildren ? (
                  <div id={portfolioTreeId}>
                    {showShowreel ? (
                      <MayaOutlinerRow
                        depth={1}
                        to="/"
                        label={showreelNavLink?.label || 'showreel'}
                        selectionTier={isShowreelRoute ? 'primary' : 'none'}
                        navIconType={showreelNavLink?.icon || 'showreel'}
                      />
                    ) : null}

                    {showProjectsRow ? (
                      <>
                        <MayaOutlinerRow
                          depth={1}
                          label="projects"
                          selectionTier={getProjectsGroupSelectionTier()}
                          mayaIconType="transform"
                          isExpandable
                          isExpanded={lockExpanded || isProjectsOpen}
                          expandLocked={lockExpanded}
                          onClick={lockExpanded ? undefined : handleToggleProjects}
                          ariaControls={projectsTreeId}
                        />

                        {lockExpanded || isProjectsOpen ? (
                          <div id={projectsTreeId}>
                            {filteredProjects.map((project) => {
                              const sidebarSubtitle =
                                project.sidebarSubtitle ?? project.categoryLabel
                              const showExplore = matchesSearch(`explore ${project.title}`)
                              const isProjectOpen = lockExpanded || openProjectIds[project.id]
                              const showProjectChildren = isProjectOpen && showExplore

                              return (
                                <div key={project.id}>
                                  <MayaOutlinerRow
                                    depth={2}
                                    label={project.title}
                                    selectionTier={getProjectSelectionTier(project.id)}
                                    project={project}
                                    isExpandable
                                    isExpanded={isProjectOpen}
                                    expandLocked={lockExpanded}
                                    onClick={() => handleNavigateToProject(project.id)}
                                    onExpandClick={() => handleToggleProjectExpand(project.id)}
                                    ariaControls={`${projectsTreeId}-${project.id}`}
                                    ariaLabel={`${project.title} — ${sidebarSubtitle}`}
                                  />
                                  {showProjectChildren ? (
                                    <div
                                      id={`${projectsTreeId}-${project.id}`}
                                    >
                                      <MayaOutlinerRow
                                        depth={3}
                                        to={getProjectDetailsPath(project.id)}
                                        label="explore"
                                        selectionTier={getExploreSelectionTier(project.id)}
                                        mayaIconType="reference"
                                        ariaLabel={`Explore ${project.title} case study`}
                                      />
                                    </div>
                                  ) : null}
                                </div>
                              )
                            })}
                          </div>
                        ) : null}
                      </>
                    ) : null}

                    {showContact ? (
                      <MayaOutlinerRow
                        depth={1}
                        to="/contact"
                        label={contactNavLink?.label || 'contact'}
                        selectionTier={isContactRoute ? 'primary' : 'none'}
                        navIconType={contactNavLink?.icon || 'contact'}
                      />
                    ) : null}

                    {showAbout ? (
                      <MayaOutlinerRow
                        depth={1}
                        to="/about"
                        label={aboutNavLink?.label || 'about'}
                        selectionTier={isAboutRoute ? 'primary' : 'none'}
                        navIconType={aboutNavLink?.icon || 'about'}
                      />
                    ) : null}

                    {showCharactersRow ? (
                      <>
                        <MayaOutlinerRow
                          depth={1}
                          label={CHARACTERS_LABEL}
                          mayaIconType="transform"
                          isExpandable
                          isExpanded={isCharactersOpen}
                          onClick={() => setIsCharactersOpen((open) => !open)}
                          ariaControls={charactersTreeId}
                        />

                        {isCharactersOpen ? (
                          <div id={charactersTreeId}>
                            {filteredCharacters.map((character) => {
                              const isCharacterOpen = openCharacterIds[character.id]
                              const showControlRig =
                                !normalizedQuery ||
                                matchesSearch('controlrig') ||
                                matchesSearch('control rig') ||
                                matchesSearch(character.label)
                              const showJointRow =
                                !normalizedQuery ||
                                matchesSearch('joints') ||
                                matchesSearch('skeleton') ||
                                matchesSearch(character.label)
                              const showCharacterChildren =
                                isCharacterOpen && (showControlRig || showJointRow)

                              return (
                                <div key={character.id}>
                                  <MayaOutlinerRow
                                    depth={2}
                                    label={character.label}
                                    mayaIconType="mesh"
                                    isExpandable
                                    isExpanded={isCharacterOpen}
                                    onClick={() => handleToggleCharacter(character.id)}
                                    ariaControls={`${charactersTreeId}-${character.id}`}
                                  />
                                  {showCharacterChildren ? (
                                    <MayaOutlinerCharacterRigRows
                                      character={character}
                                      charactersTreeId={charactersTreeId}
                                      showControlRig={showControlRig}
                                      showJointRow={showJointRow}
                                      rigOverlayByCharacter={rigOverlayByCharacter}
                                      jointOverlayByCharacter={jointOverlayByCharacter}
                                      onToggleRig={handleToggleRigControls}
                                      onToggleJoints={handleToggleJoints}
                                    />
                                  ) : null}
                                </div>
                              )
                            })}
                          </div>
                        ) : null}
                      </>
                    ) : null}
                  </div>
                ) : null}
              </>
            ) : null}

            {showDefaultSets
              ? DEFAULT_OUTLINER_SETS.filter((setName) => matchesSearch(setName)).map((setName) => (
                  <MayaOutlinerRow
                    key={setName}
                    depth={0}
                    label={setName}
                    isStatic
                    smallIcon
                    isDimmed
                    mayaIconType="set"
                  />
                ))
              : null}
          </div>
        </MayaOutlinerScrollArea>
      </div>
      </OutlinerDrawerBody>
    </nav>
  )
}

export default SidebarOutliner
